import { voiceTrace } from './ariaVoiceTrace.js';
// shimmer-web/src/streamingTranscribe.js
// ─────────────────────────────────────────────────────────────────────────
// Real-time speech-to-text using AudioWorklet + WebSocket to local Whisper.
// Whisper acts as both the speech detector AND the transcriber — no
// MediaRecorder, no browser VAD. The 4080 is fast enough to transcribe
// continuously.
//
// Usage:
//   const session = await startStreamingTranscribe({
//     onPartial: (text) => setLiveText(text),
//     onFinal: ({ text, read }) => sendToConversation(text, read),
//     onError: (err) => console.error(err),
//   });
//   session.setMuted(true);   // Aria is talking — don't listen to her
//   // later:
//   session.stop();
//
// ── THE MIC WENT DEAF AFTER HER FIRST QUESTION (Braeden 8/16) ────────────
//
//   "everytime the user trys to talk into the mic, after pressing the button
//    adn listening to arias question the mic doesnt work and it needs to work
//    instantly"
//
// whisper-server/serve.py is ONE UTTERANCE PER SOCKET — `await emit_final();
// return` ends the handler and the socket closes. This file used to answer
// that by calling stop() on every final, and stop() tore down EVERYTHING:
// mediaStream tracks stopped (that releases the mic HARDWARE), AudioContext
// closed, worklet disconnected. App.jsx then rebuilt the whole stack —
// getUserMedia, new AudioContext, fetch the worklet, addModule, new
// AudioWorkletNode, new WS — plus up to 200ms of polling before its loop even
// noticed the session had ended. Half a second to two seconds of total
// deafness after every single utterance.
//
// Worse: the mic is open while ARIA speaks, so her own voice out of the
// speakers reached whisper and fired a final. Her question was tearing the
// mic down. By the time he answered it, the stack was mid-rebuild — so the
// first thing he said after every question she asked went nowhere.
//
// Now the expensive half is built ONCE and lives as long as the mic is on.
// Only the socket recycles, because only the socket is per-utterance, and a
// localhost WS costs single-digit milliseconds. Audio captured during that
// reconnect is held in a pre-roll ring and flushed the instant it opens, so
// nothing is lost between utterances either.

// Connect through the backend proxy (same origin as the API).
// The backend opens its own paired WS to whisper:3106 internally.
// Avoids cross-port WS blocks that some browsers apply silently when
// the page origin is http://localhost:5173 but the target is :3106.
// Override with localStorage.setItem('aria.whisperWsUrl', 'ws://...') if
// you need to test the direct path.
// The ring moves with his voice: the capture publishes a level here (8/31).
import { pushMicPcm } from './ariaAudioBus';

const WHISPER_WS_URL = (() => {
  try {
    const override = localStorage.getItem('aria.whisperWsUrl');
    if (override) return override;
  } catch (_) {}
  // ── SAME ORIGIN, OR THE PHONE TALKS TO ITSELF (Braeden 8/31) ────────
  //
  // This returned a hardcoded ws://127.0.0.1:3001 - which is correct on the
  // PC and impossible anywhere else. On a phone 127.0.0.1 IS THE PHONE, so
  // the mic socket could never connect: capture started, the UI said it was
  // listening, and not one frame ever reached whisper. No transcript, no
  // turn, no reply - "it says its hearing me but its not responding".
  //
  // On top of that, ws:// from an https page is mixed content and browsers
  // refuse it outright, so even the right host would have been blocked.
  //
  // The page's own origin is always right: on the PC it is localhost:3001,
  // through the tunnel it is wss://howtoaria.com. The backend serves the app
  // AND mounts the proxy at /ws/transcribe, so they are always together.
  try {
    if (typeof window !== 'undefined' && window.location && window.location.host) {
      const scheme = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
      return scheme + window.location.host + '/ws/transcribe';
    }
  } catch (_) {}
  return 'ws://127.0.0.1:3001/ws/transcribe';
})();

function emitEvent(stage, text, meta) {
  try {
    fetch('/api/pipeline/event', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ stage, text: text || '', meta: meta || null, reqId: meta?.reqId || '' }),
    }).catch(() => {});
  } catch (_) {}
}

export async function startStreamingTranscribe(opts = {}) {
  const onPartial = opts.onPartial || (() => {});
  const onFinal = opts.onFinal || (() => {});
  const onError = opts.onError || console.error;
  const onStateChange = opts.onStateChange || (() => {});

  let stopped = false;
  let muted = false;
  let mediaStream = null;
  let audioCtx = null;
  let workletNode = null;
  let ws = null;
  let pingTimer = null;
  // Declared up here with the other timers because stop() clears it. A `let`
  // below stop()'s definition works only as long as nobody ever calls stop()
  // early, and that is not a property worth depending on.
  let muteWatchdog = null;

  // Sockets are per-utterance and get retired constantly. A retired socket can
  // still deliver a message or a close event after we've moved on, and acting
  // on those would double-send a turn or reopen a socket we deliberately shut.
  // Every socket carries the generation it was opened at; anything arriving
  // from an older generation is ignored.
  let wsGen = 0;
  let openInFlight = false;
  // A reopen that arrived while another was still connecting. Dropping it is
  // a deadlock: the in-flight one is about to discover it belongs to a retired
  // generation and throw itself away, and then nothing is listening and
  // nothing is left to notice — no socket means no close event to retry on.
  // Whoever asked last is right; the finally block below honours them.
  let reopenRequested = null;

  // PRE-ROLL. The worklet keeps producing 100ms frames while a socket is being
  // recycled. Without somewhere to put them the first fifth of a second of
  // every sentence is gone — which reads exactly like "she didn't hear me".
  const PREROLL_MAX = 20;          // 20 × 100ms = 2s ceiling, bounded memory
  let preroll = [];
  let frameSerial = 0;
  const frameIds = new WeakMap();
  const traceFrame = (frame, route) => voiceTrace.record('pcm.frame', {
    session: sessionId, generation: wsGen, frame: frameIds.get(frame) ?? null,
    bytes: frame.byteLength, route, muted, buffered: preroll.length,
  });

  // Assigned once the capture stack is up (see below). Declared out here
  // because setMuted() calls openSocket() and the two live in different
  // blocks — a function declaration inside the try{} would be invisible to it.
  let wireSocket = null;
  let openSocket = null;
  let lastCtx = '';                // her last line, re-sent to every new socket (9/2)

  // Retire the current socket WITHOUT touching the mic. This is the whole
  // point of the rewrite: the socket is cheap and per-utterance, the capture
  // stack is expensive and per-session, and they no longer die together.
  const retireSocket = () => {
    wsGen++;
    const dead = ws;
    ws = null;
    try { dead && dead.close(); } catch (_) {}
  };

  const stop = async () => {
    if (stopped) return;
    stopped = true;
    voiceTrace.record('mic.stop', { session: sessionId });
    onStateChange('stopped');
    if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
    if (muteWatchdog) { clearTimeout(muteWatchdog); muteWatchdog = null; }
    // NOT retireSocket() — deliberately. EOU asks the server to finalize what
    // it has, and the close-flush below delivers it. Closing the mic IS how he
    // sends a held utterance (PTT spec), so this is the one teardown that must
    // let a final through instead of discarding it.
    try { if (ws && ws.readyState === 1) ws.send('EOU'); } catch (_) {}
    try { workletNode && workletNode.disconnect(); } catch (_) {}
    try { mediaStream && mediaStream.getTracks().forEach(t => t.stop()); } catch (_) {}
    try { audioCtx && await audioCtx.close(); } catch (_) {}
    try { ws && ws.close(); } catch (_) {}
    preroll = [];
  };

  // ── SHE MUST NOT HEAR HERSELF ───────────────────────────────────────────
  // The mic is open while Aria speaks and her voice comes out of the speakers.
  // echoCancellation helps and does not solve it: enough leaked through to
  // finalize an utterance, which under the old code tore the mic down mid-
  // question. Muting drops the frames at the source, and retires the socket so
  // anything that leaked before the gate closed can never be finalized as his
  // turn. Unmuting opens a fresh one immediately — the instant she stops is
  // the instant he starts talking, and it has to already be listening.
  // A stuck ariaSpeaking flag used to cost a dropped turn. Now it would cost
  // the microphone — muted forever, deaf with no error, the exact silence this
  // whole fix exists to end. She has never spoken for twelve seconds; if the
  // flag says she has, the flag is wrong. Listening again is the safe failure.
  const MUTE_CEILING_MS = 12000;
  const MUTE_ABSOLUTE_MS = 36000;

  const setMuted = (next) => {
    const on = !!next;
    if (on === muted || stopped) return;
    muted = on;
    voiceTrace.record('mic.mute', { session: sessionId, muted, generation: wsGen });
    for (const frame of preroll) traceFrame(frame, 'cleared-on-mute-change');
    preroll = [];
    if (muteWatchdog) { clearTimeout(muteWatchdog); muteWatchdog = null; }
    if (on) {
      let mutedMs = 0;
      retireSocket();
      onStateChange('muted');
      const checkMute = () => {
        if (stopped || !muted) return;
        mutedMs += MUTE_CEILING_MS;
        // The shared turn owner knows whether work/audio is still pending.
        // A local watchdog must not reopen only half of the listening path.
        if (opts.shouldStayMuted?.()) {
          if (opts.isFloorHeld?.()) {
            voiceTrace.record('mic.watchdog-held', { session: sessionId, muted, floor: true });
            muteWatchdog = setTimeout(checkMute, MUTE_CEILING_MS);
            return;
          }
          if (mutedMs >= MUTE_ABSOLUTE_MS) {
            emitEvent('streaming.unstuck', 'muted past absolute ceiling — listening again', {
              reqId: sessionId, level: 'warn', ms: MUTE_ABSOLUTE_MS,
            });
            setMuted(false);
            return;
          }
          voiceTrace.record('mic.watchdog-held', { session: sessionId, muted });
          muteWatchdog = setTimeout(checkMute, MUTE_CEILING_MS);
          return;
        }
        emitEvent('streaming.unstuck', 'muted past ceiling — listening again', {
          reqId: sessionId, level: 'warn', ms: MUTE_CEILING_MS,
        });
        setMuted(false);
      };
      muteWatchdog = setTimeout(checkMute, MUTE_CEILING_MS);
    } else {
      openSocket('unmute');
      onStateChange('listening');
    }
  };

  const sessionId = Math.random().toString(36).slice(2, 8);
  // Helper: tag and rethrow with a stage marker so the catch block knows
  // exactly which step failed and can report it to the dashboard.
  const step = async (name, fn) => {
    try { return await fn(); }
    catch (e) {
      const tagged = new Error(`[${name}] ${e?.name || 'Error'}: ${e?.message || e}`);
      tagged.step = name;
      tagged.original = e;
      throw tagged;
    }
  };
  try {
    onStateChange('requesting-mic');
    emitEvent('audio.in', 'mic requested', { reqId: sessionId });
    // ── USE THE MIC HE PICKED (8/30) ────────────────────────────────────
    // This path always captured the WINDOWS-DEFAULT mic; the Settings picker
    // (aria_preferred_mic_id) only applied to enroll. When his USB mic went
    // silent, the live path stayed pinned to it no matter what he selected —
    // "im in the app, i unmute, im speaking and its not catching."
    // Preferred device first; a stale/unplugged id (devices re-enumerate as
    // "4- ..." after a replug) falls back to default instead of dying.
    const baseAudio = {
      channelCount: 1,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    };
    let preferredId = '';
    try { preferredId = localStorage.getItem('aria_preferred_mic_id') || ''; } catch (_) {}
    mediaStream = await step('getUserMedia', async () => {
      if (preferredId && preferredId !== 'default') {
        try {
          return await navigator.mediaDevices.getUserMedia({
            audio: { deviceId: { exact: preferredId }, ...baseAudio },
          });
        } catch (e) {
          emitEvent('audio.mic.fallback', `preferred mic unavailable (${e.name}) — using default`, { reqId: sessionId });
          try { localStorage.removeItem('aria_preferred_mic_id'); } catch (_) {}
        }
      }
      return navigator.mediaDevices.getUserMedia({ audio: baseAudio });
    });
    // Say WHICH mic she's actually listening to — dead air with no name is
    // undiagnosable from the user's chair.
    try {
      const lbl = (mediaStream.getAudioTracks()[0] || {}).label || 'unknown';
      emitEvent('audio.mic.ok', `mic granted: ${lbl}`, { reqId: sessionId, label: lbl });
      console.log('[voice] listening on:', lbl);
    } catch (_) {
      emitEvent('audio.mic.ok', 'mic granted', { reqId: sessionId });
    }

    onStateChange('connecting-whisper');
    audioCtx = await step('new AudioContext', async () =>
      new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 48000 }));
    // Resume the context BEFORE addModule. In Electron, audioWorklet.addModule
    // throws AbortError on contexts that are still in "suspended" state when
    // no user gesture has been observed in the AudioContext's stretch yet.
    // resume() promotes the context to 'running' (mic permission grant counts
    // as user gesture, so this works inside the streaming useEffect).
    try { if (audioCtx.state === 'suspended') await audioCtx.resume(); } catch (_) {}
    // Resolve relative to the current document — in Electron the renderer
    // loads via file://, where `window.location.origin` is "file://" and
    // a leading-slash path resolves to the filesystem root (broken).
    // `new URL('./...', window.location.href)` always lands next to the
    // index.html, which is where the worklet ships.
    const workletPath = new URL('./aria-pcm-worklet.js', window.location.href).href;
    // Electron blocks AudioWorklet.addModule() from raw file:// URLs even
    // when the path is correct (worklets require a secure context, file://
    // doesn't qualify). Workaround: fetch the worklet text and load it via
    // a blob: URL — blob: is always treated as a secure context regardless
    // of the page origin. This is the same trick most Electron apps use.
    const workletUrl = await step('audioWorklet.addModule', async () => {
      const resp = await fetch(workletPath);
      if (!resp.ok) throw new Error('worklet fetch failed: ' + resp.status);
      const code = await resp.text();
      const blobUrl = URL.createObjectURL(new Blob([code], { type: 'text/javascript' }));
      await audioCtx.audioWorklet.addModule(blobUrl);
      // Hold the revoke until after addModule resolves so the spec's
      // module fetch finishes before the blob is GC'd.
      setTimeout(() => { try { URL.revokeObjectURL(blobUrl); } catch (_) {} }, 5000);
      return blobUrl;
    });
    emitEvent('audio.worklet.ok', 'worklet loaded', { reqId: sessionId });
    const source = await step('createMediaStreamSource', async () => audioCtx.createMediaStreamSource(mediaStream));
    workletNode = await step('new AudioWorkletNode', async () => new AudioWorkletNode(audioCtx, 'aria-pcm-worklet'));
    source.connect(workletNode);
    // We do NOT connect workletNode to destination — no echo to speakers

    // ── ONE SOCKET PER UTTERANCE, ONE MIC PER SESSION ───────────────────
    // serve.py's handler is `await emit_final(); return` — the socket is
    // finished the moment it hands back a transcript. That is the server's
    // design and it is fine; what was not fine was treating the socket's
    // lifetime as the session's lifetime. wireSocket() owns everything that
    // is per-utterance, openSocket() replaces one in a few milliseconds, and
    // the mic above never notices.
    wireSocket = (sock, myGen) => {
      // ── Speculative pre-warm: when a partial transcript stays the same
      // across two consecutive emits, fire a "prewarm" fetch to the backend
      // so the response is cached by the time the final lands. Subsequent
      // duplicate partials don't re-fire — once per stable transcript.
      let lastPartialText = '';
      let finalDelivered = false; // set on real final OR close-flush — never both
      const prewarmedFor = new Set(); // texts we've already pre-warmed

      sock.onmessage = (e) => {
      // A message from a socket we already walked away from. Delivering it
      // would send a turn twice, or send her own leaked voice as his.
      if (myGen !== wsGen) { voiceTrace.record('socket.stale-message', { session: sessionId, generation: myGen }); return; }
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === 'partial' && msg.text && !stopped) {
          emitEvent('ws.partial', msg.text, { reqId: sessionId, audio_ms: msg.audio_ms });
          onPartial(msg.text);

          // Speculative pre-warm: trigger when this partial == previous partial
          // (stability signal) and we haven't pre-warmed this text yet.
          if (msg.text === lastPartialText && !prewarmedFor.has(msg.text)) {
            prewarmedFor.add(msg.text);
            // Fire-and-forget. The response will populate the backend's
            // ultra-pre LRU cache. We discard the actual audio.
            try {
              // Relative, for the same reason as the socket above: a phone
              // has no backend on its own loopback (8/31).
              fetch('/api/ai/conversation', {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({ prompt: msg.text, _prewarm: true }),
              }).catch(() => {});
              emitEvent('prewarm.fire', msg.text, { reqId: sessionId });
            } catch (_) {}
          }
          lastPartialText = msg.text;
        } else if (msg.type === 'discard') {
          finalDelivered = true;
          lastPartialText = '';
          onPartial('');
          emitEvent('ws.discard', msg.code || 'discarded capture', { reqId: sessionId, audio_ms: msg.audio_ms });
          voiceTrace.record('stt.discard', { session: sessionId, generation: myGen, reason: msg.code });
        } else if (msg.type === 'final') {
          // Whisper can send an EMPTY final while the full sentence lives in
          // the last partial (proven 8/12: 9 partials built the sentence,
          // final.text === ""). Without this fallback the whole utterance is
          // silently DROPPED — the user spoke, Aria never heard a turn.
          const _finalText = (msg.text || '').trim() || (lastPartialText || '').trim();
          finalDelivered = true;
          emitEvent('ws.final', _finalText, { reqId: sessionId, audio_ms: msg.audio_ms });
          voiceTrace.record('stt.final', { session: sessionId, generation: myGen, chars: _finalText.length });
          const _pros = msg.prosody || {};
          // Compute pace (words-per-second) from transcript length ÷ audio duration.
          // This is the only reliable source; we don't extract it on the raw PCM path.
          const _wc = _finalText.split(/\s+/).filter(Boolean).length;
          const _secs = (msg.audio_ms || 0) / 1000;
          onFinal({
            text: _finalText,
            read: {
              emotion: msg.emotion || {},
              prosody: _pros,
              quality: msg.quality ?? 0,
              // Flattened for ariaWhisperReader (expects pitch_hz/energy/pace at voiceRead top level):
              pitch_hz: typeof _pros.pitch_hz === 'number' ? _pros.pitch_hz : null,
              energy:   typeof _pros.energy   === 'number' ? _pros.energy   : null,
              intonation: _pros.intonation || '',
              pace: _secs > 0.5 ? Math.round((_wc / _secs) * 10) / 10 : null,
              // THE EAR'S OWN DOUBT, CARRIED FOR THE FIRST TIME (9/5). serve.py
              // has sent `shaky_words` - the words whisper was least sure of,
              // with a probability each - on every final since July, and no
              // client ever forwarded it, so middleware/ariaMisheardRepair and
              // now middleware/ariaSenseCheck read a field nobody sent. It
              // rides inside the voice read, which App.jsx already attaches to
              // the next turn as body.voiceRead, so no other file changes.
              shaky: Array.isArray(msg.shaky_words) ? msg.shaky_words.slice(0, 3) : [],
              // THE LATENCY WATERFALL (9/26): one turn id minted the moment the
              // ear's final lands, the ear's own wall-clock stamps (speech end,
              // EOU fired, final emitted), and when it reached this browser.
              // App.jsx lifts these into body.clientTiming; the lanes never see them.
              turnId: 'vt' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7),
              earTiming: (msg.timing && typeof msg.timing === 'object') ? msg.timing : null,
              finalReceivedAt: Date.now(),
            },
          });
          // THIS USED TO BE stop(). The server is done with this socket, and
          // that is ALL that just happened — it is not a reason to stop the
          // microphone. Hand him a fresh socket and keep listening.
          if (!stopped) openSocket('after-final');
        } else if (msg.type === 'error') {
          emitEvent('ws.err', msg.message || 'whisper error', { reqId: sessionId, level: 'err' });
          onError(new Error(msg.message || 'whisper error'));
        }
      } catch (err) {
        onError(err);
      }
      };
      sock.onclose = () => {
        // Retired on purpose (she started talking, or we already replaced this
        // socket after its final). Nothing to flush and nothing to reopen.
        if (myGen !== wsGen) return;
        // FLUSH ON CLOSE (8/12, from Braeden's live whisper-test session): the
        // socket can close WITHOUT ever sending a final — server-side close, or
        // stop()'s EOU→close race on PTT release — while the whole sentence
        // sits in the last partial. That silently DROPPED the user's turn.
        // The last partial IS the utterance; deliver it exactly once.
        if (!finalDelivered && (lastPartialText || '').trim()) {
          finalDelivered = true;
          const t = lastPartialText.trim();
          voiceTrace.record('stt.final', { session: sessionId, generation: myGen, chars: t.length, reason: 'close-flush' });
          emitEvent('ws.final', t, { reqId: sessionId, flushedOnClose: true });
          try {
            onFinal({ text: t, read: { emotion: {}, prosody: {}, quality: 0, pitch_hz: null, energy: null, intonation: '', pace: null,
              turnId: 'vt' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7), earTiming: null, finalReceivedAt: Date.now(), closeFlush: true } });
          } catch (_) {}
        }
        if (stopped) return;
        onStateChange('closed');
        // The server hung up on its own — get back on the air.
        openSocket('server-close');
      };
    };

    // Opens a socket and puts it on the air. Retries a couple of times because
    // a refused connect mid-conversation should cost milliseconds, not the
    // session — only a socket that genuinely will not open stops the mic.
    openSocket = async function (why) {
      if (stopped || muted) return;
      if (openInFlight) { reopenRequested = why || 'queued'; return; }
      openInFlight = true;
      const myGen = ++wsGen;
      voiceTrace.record('socket.open-request', { session: sessionId, generation: myGen, reason: why });
      try {
        for (let attempt = 0; ; attempt++) {
          try {
            const sock = new WebSocket(WHISPER_WS_URL);
            sock.binaryType = 'arraybuffer';
            const tWsOpen = performance.now();
            await new Promise((resolve, reject) => {
              sock.onopen = resolve;
              sock.onerror = () => reject(new Error('whisper websocket failed to open'));
              setTimeout(() => reject(new Error('whisper websocket open timeout (5s)')), 5000);
            });
            // Someone muted or stopped us while this was in flight — that
            // decision is newer than this socket, so throw the socket away.
            if (stopped || muted || myGen !== wsGen) { try { sock.close(); } catch (_) {} return; }
            wireSocket(sock, myGen);
            ws = sock;
            voiceTrace.record('socket.ready', { session: sessionId, generation: myGen });
            // a fresh socket knows nothing: give it her last line again (9/2)
            try { if (lastCtx) sock.send('CTX:' + lastCtx); } catch (_) {}
            emitEvent('ws.connect', 'whisper ws open', {
              reqId: sessionId, ms: Math.round(performance.now() - tWsOpen), why: why || 'initial',
            });
            // Everything he said while we were between sockets, in order,
            // before the first live frame lands.
            if (preroll.length) {
              const held = preroll; preroll = [];
              for (const frame of held) { try { sock.send(frame); traceFrame(frame, 'flushed'); } catch (_) { traceFrame(frame, 'send-error'); } }
            }
            return;
          } catch (e) {
            if (stopped || muted || myGen !== wsGen) return;
            if (attempt >= 2) throw e;
            await new Promise((r) => setTimeout(r, 120 * (attempt + 1)));
          }
        }
      } catch (e) {
        emitEvent('streaming.err', e.message || String(e), {
          reqId: sessionId, level: 'err', step: 'ws-reopen', why: why || 'initial',
        });
        onError(e);
        // Give the mic up so App.jsx's loop can rebuild or fall back — a
        // session that cannot reach whisper is worse than no session, because
        // it holds the device while hearing nothing.
        await stop();
      } finally {
        openInFlight = false;
        const again = reopenRequested;
        reopenRequested = null;
        // Only if this attempt left us with nothing on the air. If it opened a
        // socket, the queued request was for a generation that no longer
        // matters and honouring it would churn a live one.
        if (again && !stopped && !muted && !ws) openSocket(again);
      }
    };

    await step('WebSocket connect', () => openSocket('initial'));
    if (!ws) throw new Error('whisper websocket failed to open');

    // Pipe PCM chunks from worklet → WebSocket
    workletNode.port.onmessage = (ev) => {
      if (voiceTrace.enabled()) frameIds.set(ev.data, ++frameSerial);
      if (stopped) { traceFrame(ev.data, 'stopped'); return; }
      // The RING moves with his voice (8/31). Published before the mute check
      // on purpose: while she is talking the capture is muted, and a level
      // that kept climbing would make the ring dance to her own audio.
      try { if (!muted) pushMicPcm(ev.data); } catch (_) {}
      // Aria is talking. Her voice is not his turn.
      if (muted) { traceFrame(ev.data, 'muted'); return; }
      if (ws && ws.readyState === 1) {
        try { ws.send(ev.data); traceFrame(ev.data, 'sent'); }
        catch (error) { traceFrame(ev.data, 'send-error'); throw error; }
        return;
      }
      // Between sockets. Hold it — this is the top of his sentence.
      preroll.push(ev.data);
      traceFrame(ev.data, 'buffered');
      if (preroll.length > PREROLL_MAX) traceFrame(preroll.shift(), 'overflow');
    };

    // Keepalive ping every 5s so the WS stays alive if user is silent
    pingTimer = setInterval(() => {
      if (ws && ws.readyState === 1) {
        try { ws.send(new ArrayBuffer(0)); } catch (_) {}
      }
    }, 5000);

    onStateChange('listening');
    // isActive stays true for the whole time the mic is open now — it is the
    // SESSION's liveness, not the socket's. App.jsx polls this to decide
    // whether to rebuild; it used to go false after every sentence, which is
    // what made it rebuild the mic after every sentence.
    // ── CONTEXT CLUES FOR THE EAR (Braeden 9/2) ──────────────────────────
    // Her last line goes to the transcribe server as a `CTX:` frame; it
    // rides in the decoder's prompt so what the person says back decodes
    // as an answer to it. Sockets are per-utterance, so the last context is
    // kept and re-sent on every open (see openSocket).
    const sendContext = (text) => {
      const t = String(text || '').replace(/\s+/g, ' ').trim().slice(0, 300);
      if (!t) return;
      lastCtx = t;
      try { if (ws && ws.readyState === 1) ws.send('CTX:' + t); } catch (_) {}
    };
    return { stop, isActive: () => !stopped, setMuted, isMuted: () => muted, sendContext };
  } catch (err) {
    // Report exactly which step failed so the dashboard can show the bottleneck
    // without the user needing to open DevTools.
    emitEvent('streaming.err', err.message || String(err), {
      reqId: sessionId,
      level: 'err',
      step: err.step || 'unknown',
      name: err.original?.name || err.name,
    });
    onError(err);
    await stop();
    throw err;
  }
}
